import { api } from '../api.ts';
import { useGame, money, fmt } from '../state.tsx';
import { Loading, useLoad, go, ItemIcon, ItemName, Medals, MasteryBar } from '../components/ui.tsx';
import { JobScene, StatIcon } from '../art/Scenes.tsx';
import { SandwichButton } from './Fight.tsx';
import { TierBossCard } from './Bosses.tsx';

function LockIcon() {
  return <svg className="lock-icon" width="10" height="12" viewBox="0 0 11 13" aria-hidden="true"><rect x="1" y="6" width="9" height="7" rx="1" fill="currentColor" /><path d="M3 6V4a2.5 2.5 0 015 0v2" stroke="currentColor" fill="none" strokeWidth="1.5" /></svg>;
}
export function BoltIcon() {
  return <svg className="bolt" width="10" height="13" viewBox="0 0 12 14" aria-hidden="true"><path d="M7 0L1 8h4l-1 6 6-8H6z" fill="currentColor" /></svg>;
}

export function JobsPage({ tierId }: { tierId?: string }) {
  const { me, content, byId, act, setFlash } = useGame();
  // Reload when energy or level changes so "You need more energy" is never stale (e.g. after a Pastrami Sandwich).
  const { data, reload } = useLoad(() => api.get('/api/jobs'), [me?.energy, me?.level]);
  if (!data || !me) return <Loading />;
  const unlocked = data.tiers.filter((t: any) => t.unlocked);
  const current = data.tiers.find((t: any) => t.tierId === tierId) ?? unlocked[unlocked.length - 1];
  const tier = byId.tiers.get(current.tierId);
  const nextLocked = data.tiers.find((t: any) => !t.unlocked);

  const doJob = async (job: any) => {
    const r = await act('/api/jobs/do', { jobId: job.id });
    if (!r) return;
    const lines = [
      <span key="c">You earned <span className="money">{money(r.cash)}</span> and <span className="xp">{r.xp} experience</span>.</span>,
    ];
    for (const l of r.loot) lines.push(<div key={l.itemId} className="item-cell" style={{ marginTop: 4 }}><ItemIcon itemId={l.itemId} size={28} /> You found: <ItemName itemId={l.itemId} />{l.qty > 1 ? ` ×${l.qty}` : ''}</div>);
    if (r.tierMastered) lines.push(<div key="m" className="gold" style={{ marginTop: 4 }}>★ Tier mastered! You received <ItemName itemId={r.tierMastered.rewardItem} /> and {r.tierMastered.skillPoints} skill point(s).</div>);
    for (const u of r.levelUps) lines.push(<div key={`l${u.level}`} className="gold" style={{ marginTop: 4 }}>LEVEL UP! You are now level {u.level}. <a onClick={() => go(`profile/${me.id}`)}>Spend your {u.skillPoints} skill points</a>.</div>);
    setFlash({ kind: 'good', title: `${job.name}: Job complete!`, text: <>{lines}</> });
    reload();
  };

  return (
    <div>
      <div className="tier-tabs" role="tablist" aria-label="Job tiers">
        {data.tiers.map((t: any) => {
          const def = byId.tiers.get(t.tierId);
          return (
            <a key={t.tierId} role="tab" aria-selected={t.tierId === current.tierId} className={`tier-tab ${t.tierId === current.tierId ? 'active' : ''} ${t.unlocked ? '' : 'locked'}`}
              onClick={() => (t.unlocked ? go(`jobs/${t.tierId}`) : setFlash({ kind: 'bad', text: `${def.name} jobs unlock at level ${def.minLevel}.` }))}>
              <span className="tier-tab-name">{!t.unlocked && <LockIcon />}{def.name} {t.masteryLevel > 0 && <Medals level={t.masteryLevel} />}</span>
              <span className="tier-tab-sub">{t.unlocked ? `(Level ${def.minLevel}+)` : `Locked · Level ${def.minLevel}`}</span>
            </a>
          );
        })}
      </div>
      {nextLocked && <div className="unlock-note">Unlock <span className="gold">{byId.tiers.get(nextLocked.tierId).name}</span> jobs when you reach level <span className="money">{byId.tiers.get(nextLocked.tierId).minLevel}</span>.</div>}
      <div className="tier-banner">
        <JobScene scene={tier.scene} tierId={tier.id} />
        <div className="tier-text">
          <div className="tier-name">{tier.name}</div>
          <div className="small">New York · Levels {tier.minLevel}+ · Mastery <Medals level={current.masteryLevel} /> · Master every job in this tier for a reward.</div>
        </div>
      </div>
      {me.sandwiches > 0 && <div className="right" style={{ marginBottom: 6 }}><SandwichButton /></div>}
      <table className="list stack jobs-table">
        <thead>
          <tr><th>Description</th><th>Payout</th><th>Cost</th><th>This job requires…</th><th></th></tr>
        </thead>
        <tbody>
          {current.jobs.map((jp: any) => {
            const job = byId.jobs.get(jp.jobId);
            const blocked = jp.blocker;
            return (
              <tr key={job.id}>
                <td className="job-desc">
                  <div className="job-name">{job.name} {job.focus === 'cash' && <span className="tag money-tag" title="Pays ~25% more cash, ~20% less XP">$ Money job</span>}{job.focus === 'xp' && <span className="tag xp-tag" title="Pays ~25% more XP, ~20% less cash">★ XP job</span>}</div>
                  <div className="job-mastery"><StatIcon kind="xp" /> Mastery {jp.mastery}% · done {fmt(jp.timesDone)}×</div>
                  <MasteryBar pct={jp.mastery} />
                </td>
                <td className="job-pay">
                  <div className="money big-money">{money(job.cash)}</div>
                  <div>Experience: <b className="xp-num">+{job.xp}</b></div>
                  {job.loot.length > 0 && <div className="loot-chance" title={`Possible loot: ${job.loot.map((l: any) => byId.items.get(l.itemId)?.name).join(', ')}`}>Chance to loot <span className="loot-q">?</span></div>}
                </td>
                <td className="job-cost">
                  <span className="req"><b>{job.energy}</b> <StatIcon kind="energy" /> Energy</span>
                  {job.mafia > 0 && <span className={`req ${me.mafiaSize < job.mafia ? 'missing' : ''}`}><b>{job.mafia}</b> <StatIcon kind="mafia" /> Mafia</span>}
                </td>
                <td className="job-reqs">
                  {job.requires.length === 0 ? <span className="dim small">No items needed</span> : job.requires.map((r: any) => (
                    <span key={r.itemId} className={`req-item ${r.consumed ? 'used' : ''}`} title={`${byId.items.get(r.itemId)?.name}${r.consumed ? ' (used up by the job)' : ''}`}>
                      <ItemIcon itemId={r.itemId} size={38} />
                      <span>X {r.qty}{r.consumed && '*'}</span>
                    </span>
                  ))}
                  {blocked && <div className="small bad">{blocked}</div>}
                </td>
                <td className="right job-go">
                  <button className={`btn do-job ${blocked ? 'disabled' : ''}`} onClick={() => doJob(job)}><BoltIcon />Do Job</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {current.unlocked && <TierBossCard tierId={current.tierId} jobs={current.jobs} />}
      <p className="small dim">* item is used up by the job. Items you need can be bought in the <a onClick={() => go('inventory/store')}>store</a>. {content.jobs.length} jobs in New York.</p>
    </div>
  );
}
